import {cacheMemo} from '@packages/utilities/memo';
import {resolveInnerNodeByPath} from './innerNodePath';

/**
 * True when the given node is a `<code-island>` host rendered directly inside a
 * frame viewport, in which case the host element itself is the measurable node
 * (its shadow content has no separate measurable wrapper).
 */
export const shouldUseCodeIslandHostForMeasurement = (
  nativeNode: HTMLElement
): boolean => {
  return (
    nativeNode.tagName.toLowerCase() === 'code-island' &&
    nativeNode.parentElement?.hasAttribute('data-wf-frame-viewport') === true
  );
};

const resolveNodeInShadowRoot = (
  shadowRoot: ShadowRoot,
  id: string | null | undefined
): HTMLElement | null => {
  // When there is no inner-node id (e.g. hovering/selecting the code component
  // itself rather than an element inside it), skip the inner-node lookup and
  // fall through to the host-measurement fallback below.
  const innerElem = id != null ? resolveInnerNodeByPath(shadowRoot, id) : null;
  if (innerElem?.isConnected) {
    return innerElem;
  }

  const host = shadowRoot.host as HTMLElement;
  if (shouldUseCodeIslandHostForMeasurement(host)) {
    return host;
  }

  return null;
};

/**
 * Builds a shadow-root resolver for a code component's inner node, suitable for
 * passing as `GetMeasurableNativeNodeByNativeOptions.resolveInShadowRoot`. This
 * keeps code-component-specific shadow DOM resolution inside code-libraries,
 * while the core measurement util only knows about the generic hook.
 *
 * Cache size 10: the resolver is shared across call sites (hover outline,
 * pan-to-selection) that may request different inner-node ids, so a size-1 LRU
 * would thrash and hand back a fresh closure each render — breaking the
 * referential stability `useOutline` relies on.
 */
export const codeComponentShadowNodeResolver = cacheMemo(10)(
  (nodeId: string | null | undefined) => (shadowRoot: ShadowRoot) => {
    return resolveNodeInShadowRoot(shadowRoot, nodeId);
  }
);
